import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

interface RestrictionItem {
  key: string;
  label: string;
}

interface RestrictionChoice {
  key: string;
  message: string;
}

interface RestrictionPayload {
  company: { id: number; name: string; collection_mode: 'prepaid' | 'on_account' };
  catalog: RestrictionItem[];
  configuration: {
    restriction_mode: 'automatic' | 'manual';
    manual_until: string | null;
    manual_restrictions: RestrictionChoice[];
  };
  access: {
    access_state: string;
    reason: string | null;
    access_enforcement_enabled: boolean;
    restriction_enforced: boolean;
    restriction_mode: 'automatic' | 'manual';
    auto_access_state: string;
    auto_restriction_phase: string | null;
    restrictions: RestrictionChoice[];
    overdue_days: number;
  };
}

interface FormValues {
  restriction_mode: 'automatic' | 'manual';
  manual_until?: Dayjs;
  restriction_keys: string[];
  messages: Record<string, string>;
  revision_reason: string;
}

interface Props {
  open: boolean;
  company: { id: number; name: string } | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CompanySubscriptionRestrictionsModal: React.FC<Props> = ({
  open,
  company,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [messageApi, messageContext] = message.useMessage();
  const [data, setData] = useState<RestrictionPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const mode = Form.useWatch('restriction_mode', form);
  const selectedKeys = Form.useWatch('restriction_keys', form) || [];

  const load = useCallback(async (signal: AbortSignal) => {
    if (!open || !company) return;
    setLoading(true);
    setData(null);
    try {
      const response = await fetch(`/api/subscription/companies/${company.id}/restrictions`, {
        signal,
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Impossible de charger les restrictions.');
      }
      const next = payload.data as RestrictionPayload;
      if (signal.aborted) return;
      setData(next);
      const isActiveManual = next.access.restriction_mode === 'manual';
      const choices = isActiveManual ? next.configuration.manual_restrictions : [];
      form.setFieldsValue({
        restriction_mode: isActiveManual ? 'manual' : 'automatic',
        manual_until: isActiveManual && next.configuration.manual_until
          ? dayjs(next.configuration.manual_until)
          : undefined,
        restriction_keys: choices.map(({ key }) => key),
        messages: Object.fromEntries(choices.map(({ key, message: text }) => [key, text])),
        revision_reason: '',
      });
    } catch (error) {
      if (signal.aborted) return;
      messageApi.error(error instanceof Error ? error.message : 'Chargement impossible.');
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [company, form, messageApi, open]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const save = useCallback(async (values: FormValues) => {
    if (!company || loading || data?.company.id !== company.id) return;
    setSaving(true);
    try {
      const response = await fetch(`/api/subscription/companies/${company.id}/restrictions`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          restriction_mode: values.restriction_mode,
          manual_until: values.restriction_mode === 'manual'
            ? values.manual_until?.toISOString()
            : null,
          manual_restrictions: values.restriction_mode === 'manual'
            ? (values.restriction_keys || []).map((key) => ({
                key,
                message: values.messages?.[key]?.trim(),
              }))
            : [],
          revision_reason: values.revision_reason,
        }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Impossible d’enregistrer les restrictions.');
      }
      messageApi.success('Restrictions de souscription enregistrées.');
      onSuccess();
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }, [company, data, loading, messageApi, onSuccess]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Enregistrer les restrictions"
      confirmLoading={saving}
      okButtonProps={{ disabled: loading || !data || data.company.id !== company?.id }}
      width={820}
      title={`Restrictions de souscription — ${company?.name || 'Entreprise'}`}
    >
      {messageContext}
      <Spin spinning={loading}>
        {data && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              showIcon
              type={data.access.restrictions.length ? 'warning' : 'info'}
              message={
                <Space wrap>
                  <span>{data.access.restriction_enforced || data.access.restrictions.length > 0
                    ? 'État appliqué'
                    : 'État calculé'} : {data.access.access_state}</span>
                  <Tag>{data.access.restriction_mode === 'manual' ? 'Manuel' : 'Automatique'}</Tag>
                </Space>
              }
              description={
                `Retard maximal : ${data.access.overdue_days} jour(s). ` +
                `Si le mode automatique s'applique : ${data.access.auto_access_state}` +
                `${data.access.auto_restriction_phase ? ` (${data.access.auto_restriction_phase})` : ''}.`
              }
            />
            {data.access.restriction_mode === 'automatic' &&
              data.access.access_enforcement_enabled === false &&
              ['overdue', 'credit_limit', 'both'].includes(data.access.reason || '') && (
                <Alert
                  showIcon
                  type="warning"
                  message="Restrictions financières en observation"
                  description="Le réglage global d’application est désactivé. Les délais et l’état automatique sont calculés, mais aucune restriction financière automatique n’est appliquée."
                />
              )}
            <Alert
              showIcon
              type="info"
              message="Dérogation financière temporaire"
              description="Le SysAdmin peut choisir le mode manuel pour toute entreprise, prépayée ou à compte. À la date de fin, le niveau automatique dû reprend selon les conditions de paiement."
            />
            <Form form={form} layout="vertical" onFinish={save}>
              <Form.Item name="restriction_mode" label="Mode de restriction" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'automatic', label: 'Automatique — selon les conditions de souscription' },
                  { value: 'manual', label: 'Manuel — sélection temporaire du SysAdmin' },
                ]} />
              </Form.Item>

              {mode === 'manual' && (
                <>
                  <Form.Item
                    name="manual_until"
                    label="Fin du mode manuel"
                    extra="Date révisable avant son échéance. À cette date, les règles automatiques reprennent."
                    rules={[
                      { required: true, message: 'Choisissez une date de fin.' },
                      { validator: (_rule, value?: Dayjs) => !value || value.isAfter(dayjs())
                        ? Promise.resolve()
                        : Promise.reject(new Error('La date de fin doit être future.')) },
                    ]}
                  >
                    <DatePicker
                      showTime
                      format="DD/MM/YYYY HH:mm"
                      style={{ width: '100%' }}
                      disabledDate={(date) => date.isBefore(dayjs(), 'day')}
                    />
                  </Form.Item>
                  <Form.Item name="restriction_keys" label="Actions à restreindre">
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Space direction="vertical">
                        {data.catalog.map(({ key, label }) => (
                          <Checkbox key={key} value={key}>{label}</Checkbox>
                        ))}
                      </Space>
                    </Checkbox.Group>
                  </Form.Item>
                  {data.catalog.filter(({ key }) => selectedKeys.includes(key)).map(({ key, label }) => (
                    <Form.Item
                      key={key}
                      name={['messages', key]}
                      label={`Message public — ${label}`}
                      rules={[
                        { required: true, whitespace: true, message: 'Rédigez le message affiché aux utilisateurs.' },
                        { min: 15, max: 500, message: 'Entre 15 et 500 caractères.' },
                      ]}
                    >
                      <TextArea rows={2} maxLength={500} showCount />
                    </Form.Item>
                  ))}
                  <Text type="secondary">
                    Seules les actions cochées seront restreintes. Les blocages de licence expirée
                    et les suspensions administratives continuent de s'appliquer.
                  </Text>
                </>
              )}
              <Form.Item
                name="revision_reason"
                label="Motif interne de la décision"
                style={{ marginTop: 16 }}
                rules={[
                  { required: true, whitespace: true, message: 'Un motif est obligatoire.' },
                  { min: 3, max: 500, message: 'Entre 3 et 500 caractères.' },
                ]}
              >
                <TextArea rows={2} maxLength={500} showCount />
              </Form.Item>
            </Form>
          </Space>
        )}
      </Spin>
    </Modal>
  );
};

export default CompanySubscriptionRestrictionsModal;
